import type { ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "../../lib/cn";

export type ModalSize = "sm" | "md" | "lg";

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  /** The scrolling body. Optional: a confirm dialog is title + description + actions only. */
  children?: ReactNode;
  size?: ModalSize;
  /**
   * Actions pinned below the scroll area — Cancel/Save and friends.
   *
   * Passing them here rather than as the last child is what keeps them on screen: `children`
   * scrolls, this does not. A dialog whose buttons scrolled out of view looked, at a glance,
   * like a dialog with no way to submit.
   */
  footer?: ReactNode;
}

const sizeClasses: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
};

/** ≈ a Bootstrap/Radix modal; also serves as the base for slide-over "Sheet" usage via size. */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = "md",
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Portal>
        {/* backdrop-blur separates the dialog from a busy table behind it without a heavier scrim */}
        <Dialog.Overlay
          className={cn(
            "fixed inset-0 z-50 bg-black/60 backdrop-blur-[2px]",
            "data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out",
          )}
        />
        <Dialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2",
            "rounded-card border border-border bg-surface shadow-lg",
            "focus:outline-none",
            // The keyframes carry their own translate(-50%, -50%), so they must not fight the
            // static centring above — both agree on the final transform.
            "data-[state=open]:animate-content-in data-[state=closed]:animate-content-out",
            // The dialog is centred by translating it up half its own height, so content taller
            // than the viewport used to overflow BOTH edges at once — the title disappeared off
            // the top while the buttons fell off the bottom, with nothing scrollable and no way
            // to reach either. Browser zoom below 100% made it worse by shrinking the viewport
            // in CSS pixels while the content kept its size.
            //
            // Bounding the height and laying the dialog out as a column fixes that: the header
            // is pinned, and any overflow is confined to the body below it.
            "flex max-h-[calc(100dvh-2rem)] flex-col",
            sizeClasses[size]
          )}
        >
          {/* shrink-0 so a long title cannot squeeze the header instead of scrolling the body */}
          <div className="flex shrink-0 items-start justify-between gap-4 p-6 pb-4">
            <div>
              <Dialog.Title className="text-lg font-semibold text-text">
                {title}
              </Dialog.Title>
              {description && (
                <Dialog.Description className="mt-1 text-sm text-text-muted">
                  {description}
                </Dialog.Description>
              )}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="rounded-control p-1 text-text-muted transition-colors hover:bg-surface-hover hover:text-text"
            >
              <X className="size-5" />
            </Dialog.Close>
          </div>

          {/*
            overscroll-contain stops a wheel gesture that reaches the end of this list from
            chaining to the page underneath, which scrolls the background behind the overlay.
          */}
          {children && (
            <div
              className={cn(
                "scrollbar-slim flex-1 overflow-y-auto overscroll-contain px-6",
                footer ? "pb-4" : "pb-6"
              )}
            >
              {children}
            </div>
          )}

          {footer && (
            <div className="shrink-0 border-t border-border px-6 py-4">
              {footer}
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
